/**
 * Cookie consent — the browser side of `features.cookieConsent` (MiLicita).
 *
 * On a deployment WITHOUT the flag (UZ, KZ) every function here answers «yes,
 * allowed» and nothing is ever read or written: `hasConsent()` short-circuits on
 * a module constant, so every tracker loads exactly as it always has.
 *
 * On a deployment WITH it, no analytics or advertising technology may store or
 * read anything on the device, or talk to its vendor, until the visitor grants
 * that category (LSSI-CE art. 22.2; AEPD «Guía sobre el uso de las cookies»,
 * May 2024). Every tracker entry point asks `hasConsent(category)` first:
 *
 *  - `analytics` — PostHog (`initPostHog`, `phCapture`), GA4 (`hasGtag`), the
 *    first-party funnel beacon `POST /events/collect` and `POST /events/track`
 *    (analytics cookies are NOT exempt in Spain — guía §2.1.2 c), and the
 *    first-touch UTM / referrer memory.
 *  - `marketing` — the Meta Pixel (`hasFbq`), the `_fbp`/`_fbc` cookies, the
 *    `fbclid` / Google click-id memory and the CAPI bridge `POST /meta/events`.
 *
 * `tenders_vid` (the visitor id all three systems join on) exists only while at
 * least one of the two is granted.
 *
 * Kept free of tracker imports so `tracking.ts`, `posthog.ts`, `funnel.ts` and
 * `meta-cookies.ts` can all read it without a cycle; starting and stopping the
 * trackers on a change lives in `consent-trackers.ts`.
 */
import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE_DAYS,
  readConsentCookie,
  serializeConsent,
  type ConsentCategory,
  type ConsentChoice,
  type CookieConsent,
} from '@tenders/shared';
import { countryConfig } from '../country';

export type { ConsentCategory, ConsentChoice, CookieConsent };

/** Window event: a choice was just saved (detail = the new choice). */
export const CONSENT_CHANGE_EVENT = 'uzt:consent-change';
/** Window event: reopen the banner on its settings layer (the footer link). */
export const CONSENT_OPEN_EVENT = 'uzt:consent-open';

// Optional chaining on purpose: several unit tests mock `../country` with a
// partial config, and a missing `features` must read as «not required».
const REQUIRED =
  (countryConfig as Partial<typeof countryConfig> | undefined)?.features?.cookieConsent === true;

/** Does this deployment ask for cookie consent at all? */
export function consentRequired(): boolean {
  return REQUIRED;
}

/** The visitor's current choice, or `null` when there is none that still counts. */
export function readConsent(): CookieConsent | null {
  if (!REQUIRED || typeof document === 'undefined') return null;
  try {
    return readConsentCookie(document.cookie);
  } catch {
    return null;
  }
}

/**
 * May `category` run? Always `true` where consent is not required (UZ/KZ);
 * otherwise only after the visitor granted it — no choice grants nothing, and
 * during SSR/prerender there is no visitor, so nothing is granted.
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (!REQUIRED) return true;
  return readConsent()?.[category] === true;
}

/** Should the banner's first layer be shown on this page load? */
export function needsConsentPrompt(): boolean {
  return REQUIRED && typeof document !== 'undefined' && readConsent() === null;
}

/** The registrable host of this deployment (`milicita.es`). */
function siteApex(): string {
  try {
    return new URL(countryConfig.brand.siteUrl).host;
  } catch {
    return '';
  }
}

/**
 * `; domain=.<apex>` when the page is served from the site's own host, so the
 * API on `api.<apex>` receives the choice on its credentialed requests (the
 * server-side CAPI / analytics gate). Host-only elsewhere (localhost, previews).
 */
export function cookieDomainAttr(): string {
  const apex = siteApex();
  if (!apex || typeof location === 'undefined') return '';
  const host = location.hostname;
  return host === apex || host.endsWith(`.${apex}`) ? `; domain=.${apex}` : '';
}

/** Store the choice (first-party, readable by the API) and announce it. */
export function writeConsent(choice: ConsentChoice): void {
  if (!REQUIRED || typeof document === 'undefined') return;
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  try {
    document.cookie =
      `${CONSENT_COOKIE}=${serializeConsent(choice)}; path=/; max-age=${CONSENT_MAX_AGE_DAYS * 86400}` +
      `${cookieDomainAttr()}${secure}; SameSite=Lax`;
  } catch {
    /* cookies blocked — the choice holds for this page only, and the banner asks again next time. */
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: choice }));
  } catch {
    /* no CustomEvent (very old browser) — nothing listens for it there anyway. */
  }
}

/** Reopen the banner on its settings layer — the footer «Configuración de cookies». */
export function openConsentSettings(): void {
  if (!REQUIRED || typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}
